import { type Environment, fetchQuery, graphql } from 'react-relay';
import { GraphQLErrors } from '@atlassian/jira-business-graphql-errors/src/index.tsx';
// TODO HL-1430 move this util function into our package once JWM business page is replaced with ours
import { fetchActivityFeed } from '@atlassian/jira-business-summary-services/src/services/activity-feed/index.tsx';
import {
	OPEN_ISSUES_JQL,
	PAGED_DATA_PAGE_SIZE,
} from '@atlassian/jira-business-summary-services/src/services/constants.tsx';
import type {
	IssuesByAssigneeDataType,
	IssuesByTypeDataType,
} from '@atlassian/jira-business-summary-services/src/services/summary-data/types.tsx';
import type {
	PagedIssuesByAssigneeType,
	PagedIssuesByTypeType,
} from '@atlassian/jira-business-summary-services/src/services/types.tsx';
import {
	transformIssuesByAssigneeData,
	transformIssuesByTypeData,
} from '@atlassian/jira-business-summary-services/src/services/utils.tsx';
import { ISSUES_BY_ASSIGNEE_QUERY } from '@atlassian/jira-business-summary/src/services/issues-by-assignee/gql.tsx';
import { ISSUES_BY_TYPE_QUERY } from '@atlassian/jira-business-summary/src/services/issues-by-type/gql.tsx';
import {
	performPostRequest,
	performGetRequest,
} from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type { servicesEpicHierarchyNameQuery as servicesEpicHierarchyNameQueryType } from '@atlassian/jira-relay/src/__generated__/servicesEpicHierarchyNameQuery.graphql';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import type { OverviewCountType } from '../common/types.tsx';
import { fetchSummaryPageData } from './summary-data/index.tsx';
import { cacheParallelAsyncFn } from './utils.tsx';
import { transformData } from './related-projects/utils.tsx';
import type { SummaryPageDataType } from './summary-data/types.tsx';

type CountKey = 'doneThisWeek' | 'updatedThisWeek' | 'createdThisWeek' | 'dueThisWeek';

export type TotalCountMetaData = {
	totalUnresolvedIssues?: number;
};

const cachedFetchSummaryPageData = cacheParallelAsyncFn(fetchSummaryPageData);

export const projectSummaryDataSelector =
	(type: OverviewCountType) => (summaryPageData: SummaryPageDataType) => {
		const { overviewItems } = summaryPageData;

		const countKeyMapping: Record<OverviewCountType, CountKey> = {
			done: 'doneThisWeek',
			updated: 'updatedThisWeek',
			created: 'createdThisWeek',
			due: 'dueThisWeek',
		};

		const countKey = countKeyMapping[type];

		return {
			type,
			count: overviewItems?.[countKey] || 0,
		};
	};

export const fetchProjectSummaryData = async (
	cloudId: string,
	projectKey: string,
	type: OverviewCountType,
	gqlQuery: string,
) => {
	const jqlContext = `project = "${projectKey}"`;
	const summaryPageData = await cachedFetchSummaryPageData({ cloudId, jqlContext, gqlQuery });

	return projectSummaryDataSelector(type)(summaryPageData);
};

export const statusOverviewDataSelector = (summaryPageData: SummaryPageDataType) => {
	const { issuesByStatus } = summaryPageData;

	return {
		issuesByStatus: issuesByStatus || [],
	};
};

export const fetchStatusOverviewData = async (
	cloudId: string,
	projectKey: string,
	gqlQuery: string,
) => {
	const jqlContext = `project = "${projectKey}"`;
	const summaryPageData = await cachedFetchSummaryPageData({ cloudId, jqlContext, gqlQuery });

	return statusOverviewDataSelector(summaryPageData);
};

export const priorityBreakdownDataSelector = (summaryPageData: SummaryPageDataType) => {
	const { issuesByPriority } = summaryPageData;

	return {
		issuesByPriority: issuesByPriority || [],
	};
};

export const fetchPriorityBreakdownData = async (
	cloudId: string,
	projectKey: string,
	gqlQuery: string,
) => {
	const jqlContext = `project = "${projectKey}"`;
	const summaryPageData = await cachedFetchSummaryPageData({
		cloudId,
		jqlContext,
		gqlQuery,
	});

	return priorityBreakdownDataSelector(summaryPageData);
};

export const fetchActivityFeedData = async (after: string | null, projectKey: string) => {
	return fetchActivityFeed(after, projectKey);
};

export const fetchFastActivityFeedData = async (projectId: string) => {
	const response = await performGetRequest(
		`/rest/internal/data/activity-stream/project/${projectId}`,
	);
	return response;
};

export const typesOfWorkDataSelector = (summaryPageData: SummaryPageDataType) => {
	const { issuesByType } = summaryPageData;

	return (
		issuesByType || {
			pageInfo: {
				hasNextPage: false,
				endCursor: '',
			},
			data: [],
			totalUnresolvedIssues: 0,
		}
	);
};

export const fetchTypesOfWorkData = async (
	after: string | null,
	cloudId: string,
	projectKey: string,
	gqlQuery: string,
): Promise<IssuesByTypeDataType | PagedIssuesByTypeType> => {
	const jqlContext = `project = "${projectKey}"`;

	if (after === null) {
		const summaryPageData = await cachedFetchSummaryPageData({ cloudId, jqlContext, gqlQuery });

		return typesOfWorkDataSelector(summaryPageData);
	}

	const QUERY_NAME = 'IssuesByTypeData';
	const response = await performPostRequest(`/rest/gira/1/?operation=${QUERY_NAME}`, {
		body: JSON.stringify({
			query: ISSUES_BY_TYPE_QUERY,
			variables: {
				jql: `${jqlContext} AND ${OPEN_ISSUES_JQL}`,
				pageSize: PAGED_DATA_PAGE_SIZE,
				after,
			},
		}),
	});

	if (response.errors != null) {
		throw new GraphQLErrors(response.errors);
	}

	return transformIssuesByTypeData(response.data.issuesByType);
};

export const teamWorkloadDataSelector = (summaryPageData: SummaryPageDataType) => {
	const { issuesByAssignee } = summaryPageData;

	return (
		issuesByAssignee || {
			pageInfo: {
				hasNextPage: false,
				endCursor: '',
			},
			data: [],
			totalUnresolvedIssues: 0,
		}
	);
};

export const fetchTeamWorkloadData = async (
	after: string | null,
	cloudId: string,
	projectKey: string,
	gqlQuery: string,
): Promise<IssuesByAssigneeDataType | PagedIssuesByAssigneeType> => {
	const jqlContext = `project = "${projectKey}"`;

	if (after === null) {
		const summaryPageData = await cachedFetchSummaryPageData({
			cloudId,
			jqlContext,
			gqlQuery,
		});

		return teamWorkloadDataSelector(summaryPageData);
	}

	const QUERY_NAME = 'IssuesByAssigneeData';
	const response = await performPostRequest(`/rest/gira/1/?operation=${QUERY_NAME}`, {
		body: JSON.stringify({
			query: ISSUES_BY_ASSIGNEE_QUERY,
			variables: {
				jql: `${jqlContext} AND ${OPEN_ISSUES_JQL}`,
				pageSize: PAGED_DATA_PAGE_SIZE,
				after,
			},
		}),
	});

	if (response.errors != null) {
		throw new GraphQLErrors(response.errors);
	}

	return transformIssuesByAssigneeData(response.data.issuesByAssignee);
};

type EpicProgressDataResponse = {
	xLabels: Record<
		string,
		{
			key: string;
			summary: string;
			iconUrl: string;
			icon: string;
		}
	>;
	dimensionOneLabels: Record<
		string,
		{
			key: string;
			name: string;
			colour: string;
		}
	>;
	data: {
		start: number;
		total: number;
		size: number;
		values: Array<{
			xKey: string;
			dimensionalData: Array<{
				dimensionOneKey: string;
				result: {
					count: number;
				};
			}>;
		}>;
		last: boolean;
		nextPageStart: number;
	};
};

const transformEpiProgressData = (response: EpicProgressDataResponse) => {
	return response.data.values.map((value) => {
		const epic = response.xLabels[value.xKey];

		return value.dimensionalData.map((dimension) => ({
			name: epic.summary,
			key: epic.key,
			iconUrl: epic.iconUrl,
			count: dimension.result.count,
			status: dimension.dimensionOneKey,
		}));
	});
};

export const fetchEpicProgressData = async (
	environment: Environment,
	cloudId: string,
	projectKey: string,
	projectId: string,
) => {
	const [epicHierarchyNameQuery, epicProgressResponse] = await Promise.all([
		fetchQuery<servicesEpicHierarchyNameQueryType>(
			environment,
			graphql`
				query servicesEpicHierarchyNameQuery($cloudId: ID!, $projectKey: String!) {
					jira {
						jiraProjectByKey(cloudId: $cloudId, key: $projectKey) {
							issueTypes(filter: { maxHierarchyLevel: 1, minHierarchyLevel: 1 }) {
								edges {
									node {
										name
										hierarchy {
											name
										}
									}
								}
							}
						}
					}
				}
			`,
			{ cloudId, projectKey },
		).toPromise(),
		performPostRequest(`/rest/internal/data/aggregate/project/${projectId}`, {
			body: JSON.stringify({
				x: { name: 'ACTIVE_PARENT', extraParameters: {} },
				dimensionOne: { name: 'STATUS_CATEGORY', extraParameters: {} },
				aggregationType: { name: 'ISSUE_COUNT', extraParameters: {} },
				type: 'double',
				startAt: 0,
				maxResults: 100,
			}),
		}),
	]);

	const levelOneIssueTypeNames =
		epicHierarchyNameQuery?.jira?.jiraProjectByKey?.issueTypes?.edges
			?.map((edge) => edge?.node?.name)
			.filter(Boolean) || [];

	return {
		hierarchyName:
			epicHierarchyNameQuery?.jira?.jiraProjectByKey?.issueTypes?.edges?.[0]?.node?.hierarchy
				?.name || undefined,
		levelOneIssueTypeNames,
		progress: transformEpiProgressData(epicProgressResponse),
	};
};

export const fetchRelatedProjectsData = async (
	cloudId: string,
	atlassianAccountId: string | null,
	projectId: string,
) => {
	const MAX_RESULTS = 5;

	const graphResponse = await fetchJson('/gateway/api/collaboration/v2/recommend/container', {
		method: 'POST',
		body: JSON.stringify({
			context: {
				userId: atlassianAccountId,
				tenantId: cloudId,
				containerId: projectId,
				product: 'jira',
			},
			modelRequestParams: {
				experience: 'JiraContainerNearbyContainer',
				caller: 'jira',
			},
			requestingUserId: atlassianAccountId,
			expand: 'existing', // existing filters out projects that could not be hydrated
			maxNumberOfResults: MAX_RESULTS,
		}),
	});
	return transformData(graphResponse, projectId);
};

export const fetchCsatOverTimeData: () => Promise<{
	averageCsat: number | null;
	csatOverTime: {
		csat: number;
		month: string;
	}[];
}> = () =>
	// TODO https://hello.jira.atlassian.cloud/browse/CORCAP-2965
	new Promise((resolve) => {
		setTimeout(() => {
			resolve({
				averageCsat: 4.4,
				csatOverTime: [
					{ csat: 4, month: 'Jan' },
					{ csat: 5, month: 'Feb' },
					{ csat: 3, month: 'March' },
					{ csat: 5, month: 'Apr' },
					{ csat: 4, month: 'Jun' },
					{ csat: 2.5, month: 'July' },
					{ csat: 4, month: 'Aug' },
				],
			});
		}, 2000);
	});
